import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DateInput } from "./ui/date-input";

export const WITHDRAWAL_TYPES = [
  { value: "document", label: "Παραίτηση με δικόγραφο", detail: "gak", detailLabel: "ΓΑΚ δικογράφου παραίτησης" },
  { value: "extrajudicial", label: "Παραίτηση με εξώδικη δήλωση εντός της προθεσμίας των προτάσεων", detail: "date", detailLabel: "Ημερομηνία δήλωσης" },
  { value: "via_proposals", label: "Παραίτηση δια των προτάσεων", detail: "date", detailLabel: "Ημερομηνία κατάθεσης της δήλωσης" },
  { value: "new_action", label: "Παραίτηση με νέα αγωγή", detail: "gak", detailLabel: "ΓΑΚ νέας αγωγής" },
  { value: "right", label: "Παραίτηση από το δικόγραφο και από το δικαίωμα", detail: "date", detailLabel: "Ημερομηνία (προαιρετικά)" },
];
export const withdrawalLabel = (v) => WITHDRAWAL_TYPES.find((t) => t.value === v)?.label || v || "—";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("el-GR") : "—");

/** Πεδίο «Παραίτηση» (ΝΑΙ/ΟΧΙ → είδος → ΓΑΚ ή ημερομηνία) — δίπλα στη Διαδικασία στην καρτέλα υπόθεσης (iter109). */
export const WithdrawalFields = ({ editing, form, setForm, data }) => {
  const yes = editing ? !!form.withdrawal_indicator : !!data.withdrawal_indicator;
  const type = (editing ? form.withdrawal_type : data.withdrawal_type) || "";
  const spec = WITHDRAWAL_TYPES.find((t) => t.value === type);
  return (
    <>
      <div>
        <Label className="text-slate-500">Παραίτηση</Label>
        {editing ? (
          <Select value={form.withdrawal_indicator ? "yes" : "no"}
            onValueChange={(v) => setForm({ ...form, withdrawal_indicator: v === "yes", ...(v === "no" ? { withdrawal_type: "", withdrawal_gak: "", withdrawal_date: "" } : {}) })}>
            <SelectTrigger data-testid="withdrawal-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">ΟΧΙ</SelectItem>
              <SelectItem value="yes">ΝΑΙ</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <p className={`font-medium ${yes ? "text-red-700" : ""}`} data-testid="withdrawal-value">{yes ? "ΝΑΙ" : "ΟΧΙ"}</p>
        )}
      </div>
      {yes && (
        <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4 p-3 rounded-lg border border-red-200 bg-red-50/50" data-testid="withdrawal-details">
          <div>
            <Label className="text-slate-500 text-xs">Είδος Παραίτησης</Label>
            {editing ? (
              <Select value={type || "__none__"} onValueChange={(v) => setForm({ ...form, withdrawal_type: v === "__none__" ? "" : v, withdrawal_gak: "", withdrawal_date: "" })}>
                <SelectTrigger data-testid="withdrawal-type-select"><SelectValue placeholder="Επιλέξτε…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Επιλέξτε —</SelectItem>
                  {WITHDRAWAL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <p className="font-medium text-sm" data-testid="withdrawal-type-value">{withdrawalLabel(type)}</p>
            )}
          </div>
          {spec && (
            <div>
              <Label className="text-slate-500 text-xs">{spec.detailLabel}</Label>
              {editing ? (
                spec.detail === "gak"
                  ? <Input value={form.withdrawal_gak || ""} onChange={(e) => setForm({ ...form, withdrawal_gak: e.target.value })} placeholder="π.χ. 12345/2026" data-testid="withdrawal-gak-input" />
                  : <DateInput value={form.withdrawal_date || ""} onChange={(e) => setForm({ ...form, withdrawal_date: e.target.value })} data-testid="withdrawal-date-input" />
              ) : (
                <p className="font-medium text-sm" data-testid="withdrawal-detail-value">{spec.detail === "gak" ? (data.withdrawal_gak || "—") : fmt(data.withdrawal_date)}</p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

/** «Εξωδική επίλυση διαφοράς σύμφωνα με το άρθρο 214Α ΚΠολΔ» — μόνο ημερομηνία πρακτικού. */
export const Settlement214aField = ({ editing, form, setForm, data }) => (
  <div className="border-t pt-4" data-testid="settlement-214a-section">
    <Label className="text-slate-500 font-semibold">Εξωδική Επίλυση Διαφοράς (άρθρο 214Α ΚΠολΔ)</Label>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
      <div>
        <Label className="text-slate-400 text-xs">Ημερομηνία Πρακτικού</Label>
        {editing ? (
          <DateInput value={form.settlement_214a_date || ""} onChange={(e) => setForm({ ...form, settlement_214a_date: e.target.value })} data-testid="settlement-214a-date-input" />
        ) : (
          <p className={`font-medium text-sm ${data.settlement_214a_date ? "text-emerald-700" : ""}`} data-testid="settlement-214a-date-value">{fmt(data.settlement_214a_date)}</p>
        )}
      </div>
    </div>
  </div>
);

const MEDIATION_RESULTS = [
  { value: "pending", label: "Σε εξέλιξη" },
  { value: "success", label: "Επιτυχής (Πρακτικό Επιτυχούς Διαμεσολάβησης)" },
  { value: "fail", label: "Αποτυχής" },
];
export const mediationResultLabel = (v) => MEDIATION_RESULTS.find((r) => r.value === v)?.label || "—";

/** Πεδίο «Υπαγωγή σε Εκούσια Διαμεσολάβηση» (ΝΑΙ/ΟΧΙ → ημ. υπαγωγής, αποτέλεσμα, ημ. πρακτικού/λήξης) — μετράται στα Στατιστικά. */
export const MediationFields = ({ editing, form, setForm, data }) => {
  const yes = editing ? !!form.mediation_referred : !!(data.mediation_referred || data.mediation_start);
  const src = editing ? form : data;
  return (
    <div className="border-t pt-4" data-testid="mediation-section">
      <Label className="text-slate-500 font-semibold">Υπαγωγή σε Εκούσια Διαμεσολάβηση (Αναστολή Προθεσμιών)</Label>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
        <div>
          <Label className="text-slate-400 text-xs">Υπαγωγή σε Διαμεσολάβηση;</Label>
          {editing ? (
            <Select value={form.mediation_referred ? "yes" : "no"}
              onValueChange={(v) => setForm({ ...form, mediation_referred: v === "yes", ...(v === "no" ? { mediation_start: "", mediation_end: "", mediation_result: "" } : {}) })}>
              <SelectTrigger data-testid="mediation-referred-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="no">ΟΧΙ</SelectItem>
                <SelectItem value="yes">ΝΑΙ</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className={`font-medium ${yes ? "text-amber-700" : ""}`} data-testid="mediation-referred-value">{yes ? "ΝΑΙ" : "ΟΧΙ"}</p>
          )}
        </div>
        {yes && (
          <>
            <div>
              <Label className="text-slate-400 text-xs">Ημερομηνία Υπαγωγής</Label>
              {editing ? (
                <DateInput value={form.mediation_start || ""} onChange={(e) => setForm({ ...form, mediation_start: e.target.value })} data-testid="mediation-start-input" />
              ) : (
                <p className="font-medium text-sm" data-testid="mediation-start-value">{fmt(src.mediation_start)}</p>
              )}
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Αποτέλεσμα</Label>
              {editing ? (
                <Select value={form.mediation_result || "__none__"} onValueChange={(v) => setForm({ ...form, mediation_result: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="mediation-result-select"><SelectValue placeholder="Επιλέξτε…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Επιλέξτε —</SelectItem>
                    {MEDIATION_RESULTS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className={`font-medium text-sm ${src.mediation_result === "success" ? "text-emerald-600" : src.mediation_result === "fail" ? "text-red-600" : ""}`} data-testid="mediation-result-value">
                  {mediationResultLabel(src.mediation_result)}
                </p>
              )}
            </div>
            <div>
              <Label className="text-slate-400 text-xs">{src.mediation_result === "success" ? "Ημερομηνία Πρακτικού" : "Ημερομηνία Λήξης"}</Label>
              {editing ? (
                <DateInput value={form.mediation_end || ""} onChange={(e) => setForm({ ...form, mediation_end: e.target.value })} data-testid="mediation-end-input" />
              ) : (
                <p className="font-medium text-sm" data-testid="mediation-end-value">{fmt(src.mediation_end)}</p>
              )}
            </div>
          </>
        )}
      </div>
      {yes && src.mediation_start && src.mediation_end && (
        <p className="text-xs text-amber-600 mt-1">Οι δικονομικές προθεσμίες αναστέλλονται κατά τη διάρκεια της διαμεσολάβησης</p>
      )}
    </div>
  );
};
